import { KeyboardEvent, SyntheticEvent, memo, useCallback } from "react";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import type {
  PersonDetailRowViewModel,
  PersonDetailTagTone,
} from "../../models/personView.types";
import {
  DetailCheckboxCell,
  DetailDate,
  DetailDateColumn,
  DetailRange,
  DetailRow,
  DetailTimeColumn,
  DetailTotal,
  DetailWeekday,
  StatusChipRow,
  StatusChip,
  StatusColumn,
  StatusMeta,
} from "./personSummaryRow.styled";

interface PersonTimeReportRowProps {
  detailRow: PersonDetailRowViewModel;
  updatedAtLabel: string;
  isSelected: boolean;
  getTagIconColor: (tone: PersonDetailTagTone) => ColorV2;
  onOpenDetails: () => void;
  onToggleSelection: (checked: boolean) => void;
}

export const PersonTimeReportRow = memo(
  (props: PersonTimeReportRowProps): JSX.Element => {
    const {
      detailRow,
      updatedAtLabel,
      isSelected,
      getTagIconColor,
      onOpenDetails,
      onToggleSelection,
    } = props;

    const handleOpenDetailsKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.repeat) return;
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onOpenDetails();
      },
      [onOpenDetails],
    );

    const stopClickPropagation = useCallback((event: SyntheticEvent): void => {
      event.stopPropagation();
    }, []);

    const stopSelectionKeyDownPropagation = useCallback(
      (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.stopPropagation();
      },
      [],
    );

    return (
      <DetailRow
        onClick={onOpenDetails}
        role="button"
        tabIndex={0}
        aria-label={`${detailRow.dateLabel} ${detailRow.totalTimeLabel}`}
        onKeyDown={handleOpenDetailsKeyDown}
      >
        <DetailCheckboxCell
          onClick={stopClickPropagation}
          onKeyDown={stopSelectionKeyDownPropagation}
        >
          <Checkbox
            checked={isSelected ? "checked" : "unchecked"}
            onChange={onToggleSelection}
          />
        </DetailCheckboxCell>
        <DetailDateColumn>
          <DetailDate>{detailRow.dateLabel}</DetailDate>
          <DetailWeekday>{detailRow.weekdayLabel}</DetailWeekday>
        </DetailDateColumn>
        <DetailTimeColumn>
          <DetailTotal>{detailRow.totalTimeLabel}</DetailTotal>
          {detailRow.timeRows.map((timeRow, index) => (
            <DetailRange
              key={`${String(detailRow.id)}-${timeRow}-${String(index)}`}
            >
              {timeRow}
            </DetailRange>
          ))}
        </DetailTimeColumn>
        <StatusColumn>
          <StatusChipRow>
            {detailRow.deviationTag && (
              <StatusChip $tone={detailRow.deviationTag.tone}>
                <CustomIcon
                  name={detailRow.deviationTag.iconName}
                  size="17px"
                  color={getTagIconColor(detailRow.deviationTag.tone)}
                />
                {detailRow.deviationTag.text}
              </StatusChip>
            )}
            <StatusChip $tone={detailRow.statusTag.tone}>
              <CustomIcon
                name={detailRow.statusTag.iconName}
                size="17px"
                color={getTagIconColor(detailRow.statusTag.tone)}
              />
              {detailRow.statusTag.text}
            </StatusChip>
          </StatusChipRow>
          <StatusMeta>{updatedAtLabel}</StatusMeta>
        </StatusColumn>
        <CustomIcon name="v2/right-chevron" size="20px" color={ColorV2.Blue4} />
      </DetailRow>
    );
  },
);
